import { useEffect, useMemo, useState } from "react";
import { ADMIN_COMPANY_SELECT_OPTIONS } from "../constants/discoCompanies";
import { cfpBaseUrl } from "../network/urls";

const STORAGE_KEY = "eopsCompanySelectOptions";

let cachedOptions = null;
let inflightPromise = null;
const listeners = new Set();

const readStoredOptions = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
};

const writeStoredOptions = (options) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(options));
  } catch {
    // ignore storage errors
  }
};

const notifyListeners = (options) => {
  listeners.forEach((listener) => {
    try {
      listener(options);
    } catch {
      // ignore listener errors
    }
  });
};

/** Map getCompanyDetails API rows → dropdown { value, label } */
export const mapCompaniesToSelectOptions = (companies = []) =>
  (companies || [])
    .filter((company) => company?.companyCode != null && company?.companyName)
    .map((company) => ({
      value: String(company.companyCode),
      label: String(company.companyName).trim(),
    }))
    .sort((left, right) => Number(left.value) - Number(right.value));

/** Seed / refresh the shared company dropdown cache from API data */
export const setCompanySelectOptionsFromApi = (companies) => {
  const options = mapCompaniesToSelectOptions(companies);
  if (!options.length) {
    return getCompanySelectOptions({ includePitc: true });
  }
  cachedOptions = options;
  writeStoredOptions(options);
  notifyListeners(options);
  return options;
};

const resolveBaseOptions = () => {
  if (cachedOptions?.length) return cachedOptions;
  const stored = readStoredOptions();
  if (stored?.length) {
    cachedOptions = stored;
    return cachedOptions;
  }
  return ADMIN_COMPANY_SELECT_OPTIONS;
};

/**
 * Sync read for MenuItems / native options.
 * Prefers API cache, then sessionStorage, then hardcoded fallback.
 */
export const getCompanySelectOptions = ({ includePitc = true } = {}) => {
  const options = resolveBaseOptions();
  return includePitc
    ? options
    : options.filter((option) => String(option.value) !== "10");
};

export const subscribeCompanySelectOptions = (listener) => {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Ensure company options are loaded from getCompanyDetails (via allCompaniesAPI).
 * Safe to call many times — uses in-memory / session cache.
 */
export const ensureCompanySelectOptions = async (
  token,
  baseUrl = cfpBaseUrl,
) => {
  if (cachedOptions?.length) {
    return getCompanySelectOptions({ includePitc: true });
  }

  const stored = readStoredOptions();
  if (stored?.length) {
    cachedOptions = stored;
    notifyListeners(cachedOptions);
    return cachedOptions;
  }

  if (!token) {
    return ADMIN_COMPANY_SELECT_OPTIONS;
  }

  if (!inflightPromise) {
    inflightPromise = (async () => {
      try {
        const { allCompaniesAPI } = await import("./Functions");
        const companies = await allCompaniesAPI(token, baseUrl);
        if (companies?.length) {
          return setCompanySelectOptionsFromApi(companies);
        }
      } catch {
        // keep fallback
      }
      return ADMIN_COMPANY_SELECT_OPTIONS;
    })().finally(() => {
      inflightPromise = null;
    });
  }

  return inflightPromise;
};

/** React hook — loads API companies once and keeps dropdown options in sync */
export const useCompanySelectOptions = ({
  includePitc = true,
  token,
  autoFetch = true,
} = {}) => {
  const [options, setOptions] = useState(() =>
    getCompanySelectOptions({ includePitc: true }),
  );

  useEffect(() => {
    setOptions(getCompanySelectOptions({ includePitc: true }));
    return subscribeCompanySelectOptions((next) => {
      setOptions(next?.length ? next : ADMIN_COMPANY_SELECT_OPTIONS);
    });
  }, []);

  useEffect(() => {
    if (!autoFetch || !token) return undefined;
    let cancelled = false;
    ensureCompanySelectOptions(token).then((next) => {
      if (!cancelled && next?.length) setOptions(next);
    });
    return () => {
      cancelled = true;
    };
  }, [autoFetch, token]);

  return useMemo(
    () =>
      includePitc
        ? options
        : options.filter((option) => String(option.value) !== "10"),
    [options, includePitc],
  );
};
